import { useState, useEffect, useMemo } from 'react';
import { findStaleReservations, loadSnoozeMap, snoozeReservation } from '../utils/staleReservations';

/**
 * Banner suave en la vista de Resumen que lista los tratos reservados
 * sin actividad >7 días, con acciones rápidas para gestionarlos sin
 * abandonar la pantalla.
 *
 * Tres acciones por trato:
 *   - ✅ Ya se hizo  → abre el trade (onVisitFriend) para que el
 *                    usuario complete con Confirmar Intercambio.
 *   - 🔓 Liberar    → flip reserved=false directo (no toca inventario).
 *   - ⏰ Recordar después → snooze 3 días en localStorage.
 *
 * El banner se oculta cuando no quedan reservas viejas.
 */
export default function StaleReservationsBanner({
  state,
  userUid,
  onVisitFriend,
  onLiberar,
}) {
  // Re-render cuando cambia algún snooze. Usamos un counter en lugar
  // de re-leer localStorage cada render (innecesario).
  const [snoozeBump, setSnoozeBump] = useState(0);

  const stale = useMemo(() => {
    if (!state || !userUid) return [];
    const snoozeMap = loadSnoozeMap(userUid);
    return findStaleReservations(state.trades, state.friends, snoozeMap);
    // snoozeBump fuerza re-evaluación cuando el usuario clickea "Recordar después"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.trades, state?.friends, userUid, snoozeBump]);

  // Si no hay reservas viejas, no renderiza nada.
  if (stale.length === 0) return null;

  const handleSnooze = (friendUid) => {
    snoozeReservation(userUid, friendUid);
    setSnoozeBump(v => v + 1);
  };

  return (
    <div
      className="stale-reservations-banner"
      style={{
        background: 'linear-gradient(135deg, rgba(217, 119, 6, 0.10), rgba(217, 119, 6, 0.04))',
        border: '1px solid rgba(217, 119, 6, 0.35)',
        borderRadius: 14,
        padding: '14px 16px',
        marginBottom: 20,
        animation: 'viewFadeIn 0.3s ease',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 12,
        color: '#fbbf24',
        fontWeight: 700,
        fontSize: '0.95rem',
      }}>
        <span style={{ fontSize: '1.2rem' }}>🔔</span>
        <span>
          {stale.length === 1
            ? 'Tienes 1 reserva vieja'
            : `Tienes ${stale.length} reservas viejas`}
        </span>
        <span style={{
          marginLeft: 'auto',
          fontSize: '0.72rem',
          color: 'var(--text-muted)',
          fontWeight: 500,
        }}>
          sin actividad &gt; 7 días
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {stale.map(item => (
          <div
            key={item.friendUid}
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: 10,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
            }}>
              <strong style={{ color: 'var(--text-primary)', fontSize: '0.92rem' }}>
                {item.name}
              </strong>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {item.daysOld !== null
                  ? `· reservado hace ${item.daysOld} día${item.daysOld !== 1 ? 's' : ''}`
                  : '· reservado hace mucho tiempo'}
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
              {item.receiveCount > 0 && (
                <span><span style={{ color: '#4ade80', fontWeight: 600 }}>{item.receiveCount}</span> a recibir</span>
              )}
              {item.receiveCount > 0 && item.giveCount > 0 && ' · '}
              {item.giveCount > 0 && (
                <span><span style={{ color: '#60a5fa', fontWeight: 600 }}>{item.giveCount}</span> a entregar</span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
              <button
                className="btn btn-gold"
                style={{ padding: '6px 12px', fontSize: '0.78rem', fontWeight: 700 }}
                onClick={() => onVisitFriend?.(item.friendUid)}
                aria-label={`Abrir trato con ${item.name} para confirmar`}
              >
                ✅ Ya se hizo
              </button>
              <button
                className="btn"
                style={{
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  background: 'rgba(217, 119, 6, 0.12)',
                  color: '#fbbf24',
                  border: '1px solid rgba(217, 119, 6, 0.35)',
                }}
                onClick={() => onLiberar?.(item.friendUid)}
                aria-label={`Liberar reserva con ${item.name}`}
              >
                🔓 Liberar
              </button>
              <button
                className="btn"
                style={{
                  padding: '6px 12px',
                  fontSize: '0.78rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-color)',
                }}
                onClick={() => handleSnooze(item.friendUid)}
                aria-label={`Recordar trato con ${item.name} en 3 días`}
                title="Posponer recordatorio 3 días"
              >
                ⏰ Recordar después
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
